import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';

interface SessionResponse {
  loggedIn: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = '/php';
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasToken());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(private http: HttpClient) {}

  setAuthenticated(isAuth: boolean): void {
    this.isAuthenticatedSubject.next(isAuth);
  }

  checkSession(): Observable<boolean> {
    return this.http.get<SessionResponse>(`${this.apiUrl}/auth/verify_session.php`, { withCredentials: true }).pipe(
      map(result => {
        if (result.loggedIn) {
          this.setAuthenticated(true);
          return true;
        }
        // If server says not logged in, ensure state is false.
        this.setAuthenticated(false);
        return false;
      }),
      catchError(() => {
        // On any HTTP error, assume not authenticated.
        this.setAuthenticated(false);
        return of(false);
      })
    );
  }

  private hasToken(): boolean {
    return !!localStorage.getItem('admin_token') || !!sessionStorage.getItem('admin_session');
  }

  login(username: string, password: string, rememberMe: boolean = false): Observable<any> {
    return this.http.post(`${this.apiUrl}/login.php`, { 
      username, 
      password,
      remember: rememberMe 
    }).pipe(
      tap((response: any) => {
        if (response.success) {
          if (rememberMe) {
            localStorage.setItem('admin_token', response.token || 'true');
          } else {
            sessionStorage.setItem('admin_session', 'true');
          }
          this.isAuthenticatedSubject.next(true);
        }
      })
    );
  }

  logout(): Observable<any> {
    return this.http.post(`${this.apiUrl}/logout.php`, {}).pipe(
      tap(() => {
        localStorage.removeItem('admin_token');
        sessionStorage.removeItem('admin_session');
        this.isAuthenticatedSubject.next(false);
      })
    );
  }

  isAuthenticated(): boolean {
    return this.hasToken();
  }
}
